import { useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useTypedSelector } from '../reducers'
import IProject from '../model/IProject'
import {
  ICrumb,
  IResolvedTrail,
  resolveTrail
} from '../helpers/breadcrumbTrail'
import {
  getProjectDetails,
  getProjectPath,
  getProjectPathProjectId,
  getTenantId
} from '../state'

// Complete trails by folder id, for this session. Module-level so it outlives
// the page and survives history entries whose router state was replaced.
const rememberedTrails = new Map<string, ICrumb[]>()
// The site the remembered trails were seen on; they are dropped when it
// changes. Logout needs no handling, as its redirect reloads the page.
let rememberedTenantId: string | null = null

const useBreadcrumbTrail = (): IResolvedTrail => {
  const { id: routeId } = useParams()
  const { state: locationState } = useLocation()

  const tenantId = useTypedSelector<string | null>(getTenantId)
  if (tenantId !== rememberedTenantId) {
    rememberedTrails.clear()
    rememberedTenantId = tenantId
  }
  const path = useTypedSelector<IProject[] | null>(getProjectPath)
  const pathProjectId = useTypedSelector<string | null>(getProjectPathProjectId)
  const details = useTypedSelector<IProject | null>(getProjectDetails)
  const detailsName = details?.id === routeId ? details?.name : undefined

  const resolved = useMemo(
    () =>
      resolveTrail({
        routeId,
        tenantId,
        locationState,
        path,
        pathProjectId,
        detailsName,
        remembered: routeId ? rememberedTrails.get(routeId) : undefined
      }),
    [routeId, tenantId, locationState, path, pathProjectId, detailsName]
  )

  useEffect(() => {
    if (routeId && resolved.complete) {
      rememberedTrails.set(routeId, resolved.trail)
    }
  }, [routeId, resolved])

  return resolved
}

export default useBreadcrumbTrail
